import { useEffect } from 'react';
import {
  Layout,
  AppBar,
  AppBarProps,
  Loading,
  Notification,
} from 'react-admin';
import {
  AppLocationContext,
  Breadcrumb,
  BreadcrumbItem,
  ResourceBreadcrumbItems,
  useAppLocationState,
} from '@react-admin/ra-navigation';
import { useLocation } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import ImageLogo from 'assets/logo.png';
import CustomMenu from './CustomMenu';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { UIProvider } from 'providers/uiProvider';
import { SnackbarContainer, AppSnackbar } from 'components/standalone';
import { LocationProvider } from 'providers/locationProvider';
import GpsButton from 'components/GpsButton';
import { setAutomaticRefresh } from 'react-admin';
import { useDispatch } from 'react-redux';

const useNotificationStyles = makeStyles(theme => ({
  error: {
    backgroundColor: `${theme.palette.error.light} !important`,
  },
}));

const CustomNotification: React.FC<React.ComponentProps<typeof Notification>> =
  props => {
    const classes = useNotificationStyles();
    // @ts-expect-error react-admin Notification supports 'error' class but types inherit from Snackbar
    return <Notification {...props} classes={{ error: classes.error }} />;
  };

const useStyles = makeStyles({
  title: {
    color: 'black',
    flex: 1,
  },
  navbarRoot: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    display: 'flex',
    margin: '5px 0px',
  },
  appBar: {
    color: 'black',
    backgroundColor: 'white',
  },
  appBarOrange: {
    color: 'black',
    backgroundColor: 'orange',
  },
  breadCrumb: {
    marginTop: '20px',
    marginBottom: '24px',
    '@media (max-width: 850px)': {
      paddingLeft: '8px',
      paddingRight: '8px',
    },
  },
  logo: {
    width: '120px',
    aspectRatio: '5/2',
  },
});

export const ENV = process.env.REACT_APP_ENV || 'DEV';

const CustomAppBar: React.FC = (props: AppBarProps) => {
  const dispatch = useDispatch();
  dispatch(setAutomaticRefresh(false));
  const { setOpened } = UIProvider.useContainer();
  const classes = useStyles();

  useEffect(() => {
    setOpened(!!props.open);
  }, [props.open]);

  return (
    <AppBar
      className={ENV === 'SP' ? classes.appBarOrange : classes.appBar}
      {...props}
    >
      <div className={classes.navbarRoot}>
        <img src={ImageLogo} className={classes.logo} />
      </div>
      <GpsButton />
    </AppBar>
  );
};

const CustomLayout: React.FC = (props: AppBarProps) => {
  const classes = useStyles();
  const { getUser, loading } = PermissionsProvider.useContainer();
  const location = useLocation();

  useEffect(() => {
    getUser();
  }, []);

  if (loading) return <Loading loadingPrimary="" loadingSecondary="" />;

  // Custom breadcrumb component to handle resource conflicts and custom paths
  const CustomBreadcrumbItems = () => {
    const currentPath = location.pathname;
    const [appLocation] = useAppLocationState();
    const record = appLocation?.values?.record as { id?: string } | undefined;

    // If we're on subdivision-audit pages, render custom breadcrumb
    if (currentPath.includes('subdivision-audit')) {
      return (
        <BreadcrumbItem
          name="subdivision"
          label="Subdivisions"
          to="/subdivision"
        >
          {record?.id ? (
            <BreadcrumbItem
              name="edit"
              label={`Edit #${record.id}`}
              to={`/subdivision/${record.id}`}
            >
              <BreadcrumbItem name="audit" label="Pricing" />
            </BreadcrumbItem>
          ) : (
            <BreadcrumbItem name="audit" label="Pricing" />
          )}
        </BreadcrumbItem>
      );
    }

    if (currentPath.includes('region-audit')) {
      return (
        <BreadcrumbItem name="region" label="Regions" to="/region">
          {record?.id ? (
            <BreadcrumbItem
              name="edit"
              label={`Region #${record.id}`}
              to={`/region/${record.id}`}
            >
              <BreadcrumbItem name="audit" label="Pricing" />
            </BreadcrumbItem>
          ) : (
            <BreadcrumbItem name="audit" label="Pricing" />
          )}
        </BreadcrumbItem>
      );
    }

    // If we're on master-sheet-v2 pages, exclude master-sheet from breadcrumb
    if (currentPath.includes('job-master-sheet-v2')) {
      return <ResourceBreadcrumbItems resources={['job-master-sheet-v2']} />;
    }

    // If we're on master-sheet v1 pages, exclude master-sheet-v2 from breadcrumb
    if (
      currentPath.includes('job-master-sheet') &&
      !currentPath.includes('job-master-sheet-v2')
    ) {
      return <ResourceBreadcrumbItems resources={['job-master-sheet']} />;
    }

    // Default behavior for other pages
    return <ResourceBreadcrumbItems />;
  };

  return (
    <SnackbarContainer.Provider>
      <LocationProvider.Provider>
      <AppLocationContext>
        <Layout
          {...props}
          appBar={CustomAppBar}
          menu={CustomMenu}
          notification={CustomNotification}
        >
          <Breadcrumb {...props} className={classes.breadCrumb}>
            <CustomBreadcrumbItems />
          </Breadcrumb>
          {props.children}
        </Layout>
      </AppLocationContext>
      </LocationProvider.Provider>
      <AppSnackbar />
    </SnackbarContainer.Provider>
  );
};

export default CustomLayout;
